import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '../shared/alert/alert.service';
import { PopupService } from '../shared/modal/popup.service';
import * as JSZip from 'jszip';
import { BMSOutputInformation, BreedingViewOutputModel } from './breeding-view-output.model';
import { parseCSV } from '../shared/util/file-utils';
import { SingleSiteAnalysisService } from './single-site-analysis.service';
import { SingleSiteAnalysisContext } from './single-site-analysis.context';
import { finalize } from 'rxjs/internal/operators/finalize';
import { ModalConfirmComponent } from '../shared/modal/modal-confirm.component';

@Component({
    selector: 'jhi-single-site-analysis-import',
    templateUrl: 'single-site-analysis-import.component.html'
})
export class SingleSiteAnalysisImportComponent implements OnInit {

    static BMS_OUTLIER_FILENAME = 'BMSOutlier';
    static BMS_SUMMARY_FILENAME = 'BMSSummary';
    static BMS_OUTPUT_FILENAME = 'BMSOutput';
    static BMS_INFORMATION_FILENAME = 'BMSInformation';

    @ViewChild('fileUpload')
        fileUpload: ElementRef;

    fileName = '';
    codes = {};
    extensions = ['.zip'];
    selectedFileType = this.extensions[0];
    isLoading: boolean;
    output: BreedingViewOutputModel;

    constructor(
        private translateService: TranslateService,
        private modal: NgbActiveModal,
        private modalService: NgbModal,
        private alertService: AlertService,
        private siteAnalysisService: SingleSiteAnalysisService,
        private context: SingleSiteAnalysisContext
    ) {
    }

    ngOnInit(): void {
    }

    clearSelectedFile(): void {
        this.output = null;
    }

    onFileChange(evt: any) {
        const target = evt.target;
        this.fileName = target.files[0].name;

        const extension = this.fileName.substring(this.fileName.lastIndexOf('.'));
        if (this.extensions.indexOf(extension.toLowerCase()) === -1) {
            this.fileName = '';
            target.value = '';
            this.alertService.error('single-site-analysis.validation.extensions');
            return;
        }

        this.output = new BreedingViewOutputModel();
        JSZip.loadAsync(target.files[0]).then((zip) => {
            Object.keys(zip.files).forEach((filename) => {
                if (filename.startsWith(SingleSiteAnalysisImportComponent.BMS_INFORMATION_FILENAME)) {
                    this.readInformationFile(zip.files[filename]);
                } else {
                    this.readCSVFile(filename, zip.files);
                }
            });
        });
    }

    dismiss() {
        this.modal.dismiss();
    }

    import() {
        if (this.isBMSInformationCompatible()) {
            this.siteAnalysisService.hasExistingMeans(this.context.studyId, this.output).pipe(finalize(() => {
                this.isLoading = false;
            })).subscribe((response: any) => {
                if (response.body) {
                    const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
                    confirmModalRef.componentInstance.title = this.translateService.instant('single-site-analysis.validation.confirmation');
                    confirmModalRef.componentInstance.message = this.translateService.instant('single-site-analysis.validation.overwrite.message');
                    confirmModalRef.result.then(() => {
                        this.continueImport();
                    }, () => confirmModalRef.dismiss());
                } else {
                    this.continueImport();
                }
            });
        }
    }

    continueImport() {
        this.siteAnalysisService.upload(this.context.studyId, this.output).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe((response: any) => {
            this.alertService.success('single-site-analysis.validation.success');
            this.modal.close();
        });
    }

    isBMSInformationCompatible(): boolean {
        if (!this.output.bmsInformation || !this.output.meansData || !this.output.summaryStatsData) {
            this.alertService.error('single-site-analysis.validation.invalid.file');
            return false;
        }
        if (this.output.bmsInformation.StudyId !== this.context.studyId) {
            this.alertService.error('single-site-analysis.validation.not.compatible');
            return false;
        }
        return true;
    }

    readInformationFile(informationFile) {
        this.output.bmsInformation = new BMSOutputInformation;
        informationFile.async('string').then((fileData) => {
            const lines = fileData.split('\n');
            lines.forEach((line) => {
                const [key, value] = line.split('=');
                if (key && value) {
                    this.output.bmsInformation[key.trim()] = value.trim();
                }
            });
        });
    }

    readCSVFile(fileName, files) {
        if (fileName.startsWith(SingleSiteAnalysisImportComponent.BMS_OUTLIER_FILENAME)) {
            files[fileName].async('string').then((fileData) => {
                parseCSV(fileData).subscribe((value) => {
                    this.output.outliersData = value.data;
                });
            });
        } else if (fileName.startsWith(SingleSiteAnalysisImportComponent.BMS_SUMMARY_FILENAME)) {
            files[fileName].async('string').then((fileData) => {
                parseCSV(fileData).subscribe((value) => {
                    this.output.summaryStatsData = value.data;
                });
            });
        } else if (fileName.startsWith(SingleSiteAnalysisImportComponent.BMS_OUTPUT_FILENAME)) {
            files[fileName].async('string').then((fileData) => {
                parseCSV(fileData).subscribe((value) => {
                    this.output.meansData = value.data;
                });
            });
        }
    }

}

@Component({
    selector: 'jhi-single-site-analysis-import-popup',
    template: ''
})
export class SingleSiteAnalysisImportPopupComponent implements OnInit, OnDestroy {

    routeSub: any;

    constructor(
        private route: ActivatedRoute,
        private popupService: PopupService
    ) {
    }

    ngOnInit() {
        this.routeSub = this.route.params.subscribe(() => {
            this.popupService.open(SingleSiteAnalysisImportComponent as Component);
        });
    }

    ngOnDestroy() {
        if (this.routeSub) {
            this.routeSub.unsubscribe();
        }
    }
}
